import React, { ReactNode, useEffect, useMemo, useRef, Fragment } from 'react';
import cn from 'classnames';
import { Transition } from 'react-transition-group';
import KeyboardShortcut from '@components/KeyboardShortcut';
import styles from './Menus.module.scss';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import * as RadixContextMenu from '@radix-ui/react-context-menu';
import * as RadixPopover from '@radix-ui/react-popover';
import { DropdownMenuOption } from '@app/types';

type MenuVariant = 'dropdown' | 'context';

const MenuComponentsForVariant: Record<
  MenuVariant,
  typeof RadixDropdownMenu | typeof RadixContextMenu
> = {
  dropdown: RadixDropdownMenu,
  context: RadixContextMenu,
};

const renderOption = (
  MenuComponents: typeof RadixDropdownMenu | typeof RadixContextMenu,
  menuClassName: string,
  suppressFocusControlOnItems: boolean,
  nested: boolean = false,
  {
    title,
    action,
    disabled,
    icon,
    keyboard,
    className,
    divider = false,
    suffix = null,
    keepMenuOpenOnClick = false,
    active = false,
    button,
    submenu,
    focusedClassName,
  }: DropdownMenuOption,
  idx,
) => {
  let clickHandler: (event: Event) => void = () => {};
  const Icon = icon;

  if (action && !disabled) {
    clickHandler = (e) => {
      keepMenuOpenOnClick && e.preventDefault();
      action(e);
    };
  }

  if (keyboard && !suffix) {
    suffix = <KeyboardShortcut.Key className={styles.shortcutKey}>{keyboard}</KeyboardShortcut.Key>;
  }

  let renderedOption: ReactNode = (
    <Fragment key={idx}>
      {icon && (
        <Icon className={styles.icon} color="textPrimary" hover={false} hoverOpacity={false} />
      )}
      <span className={styles.label}>{title}</span>
      {suffix}
    </Fragment>
  );

  let pointerEventHandler: (e: React.PointerEvent) => void = undefined;
  if (suppressFocusControlOnItems && nested) {
    pointerEventHandler = (e) => e.preventDefault();
  }

  if (!!button) {
    renderedOption = button;
  }

  if (divider) {
    return (
      <MenuComponents.Separator
        className={cn(
          className,
          disabled && styles.disabledOption,
          divider && styles.dividerOption,
          active && styles.activeOption,
          disabled && 'disabled',
        )}
        key={idx}
      />
    );
  }

  if (submenu) {
    return (
      <MenuComponents.Sub key={idx}>
        <MenuComponents.SubTrigger
          className={cn(
            className,
            styles.item,
            disabled && styles.disabledOption,
            divider && styles.dividerOption,
            active && styles.activeOption,
            disabled && 'disabled',
          )}
        >
          {renderedOption}
        </MenuComponents.SubTrigger>
        <MenuComponents.Portal>
          <MenuComponents.SubContent className={cn(styles.menu, styles.submenu, menuClassName)}>
            {submenu.map(
              renderOption.bind(null, MenuComponents, undefined, suppressFocusControlOnItems, true),
            )}
          </MenuComponents.SubContent>
        </MenuComponents.Portal>
      </MenuComponents.Sub>
    );
  }

  return (
    <MenuComponents.Item
      key={idx}
      className={cn(
        className,
        styles.item,
        disabled && styles.disabledOption,
        divider && styles.dividerOption,
        active && styles.activeOption,
        disabled && 'disabled',
        !!button && styles.asButton,
        !!button && focusedClassName,
      )}
      onSelect={clickHandler}
      onPointerEnter={pointerEventHandler}
      onPointerMove={pointerEventHandler}
      onPointerLeave={pointerEventHandler}
    >
      {renderedOption}
    </MenuComponents.Item>
  );
};

// Renders either a Radix Dropdown or a Radix Context Menu depending on the variant prop. Dropdown menus are animated.
export default function ExpandingMenu({
  options,
  isOpen,
  menuClassName,
  menuStyle = {},
  expandFrom = 'top-right',
  before = null,
  variant = 'dropdown',
  side = 'bottom',
  suppressFocusControlOnItems = false,
  align,
}: {
  options: Array<DropdownMenuOption>;
  isOpen: boolean;
  menuClassName?: string;
  menuStyle?: Record<string, any>;
  expandFrom?: 'top-right' | 'bottom-left' | 'top-center' | 'top-left' | 'bottom-right';
  before?: ReactNode;
  variant?: MenuVariant;
  side?: 'bottom' | 'top';
  suppressFocusControlOnItems?: boolean;
  align?: 'end' | 'start' | 'center';
}) {
  // Let user search menu without triggering any hotkeys on view or app
  const handleMenuKeyDown = (e) => {
    if (e.key !== 'Escape') {
      e.stopPropagation();
    }
  };

  const MenuComponents = useMemo(() => MenuComponentsForVariant[variant], [variant]);
  const boundRenderOption = useMemo(
    () =>
      renderOption.bind(null, MenuComponents, menuClassName, suppressFocusControlOnItems, false),
    [MenuComponents],
  );

  let _align = 'end';
  if (expandFrom.includes('left')) {
    _align = 'start';
  } else if (expandFrom.includes('center')) {
    _align = 'center';
  }

  return (
    <Transition in={isOpen} timeout={{ enter: 0, exit: 200 }} mountOnEnter unmountOnExit>
      {(animationState) => (
        <MenuComponents.Content
          className={cn(styles.menuContainer, menuClassName, styles[`from-${expandFrom}`])}
          style={menuStyle}
          onKeyDown={handleMenuKeyDown}
          forceMount
          // @ts-ignore
          align={align || _align}
          side={side}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          <MenuComponents.Group
            className={cn(
              styles.menu,
              styles[`menu-${animationState}`],
              styles[`from-${expandFrom}`],
            )}
          >
            {before}
            {options.map(boundRenderOption)}
          </MenuComponents.Group>
        </MenuComponents.Content>
      )}
    </Transition>
  );
}

export function ExpandingPopoverMenu({
  isOpen,
  menuClassName,
  menuStyle = {},
  expandFrom = 'top-right',
  side = 'bottom',
  children,
  onKeyDown,
}: {
  isOpen: boolean;
  menuClassName?: string;
  menuStyle?: Record<string, any>;
  expandFrom?: 'top-right' | 'bottom-left' | 'top-center' | 'top-left';
  side?: 'bottom' | 'top';
  children: ReactNode;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const isMounted = useRef(false);
  const handleMenuKeyDown = (e) => {
    if (e.key !== 'Escape') {
      e.stopPropagation();
    }

    if (e.key === 'Escape') {
      e.preventDefault();
    }

    onKeyDown?.(e);
  };

  const onInteractOutside = (e) => {
    if (!isMounted.current) {
      e.preventDefault();
    }
  };

  let align = 'end';
  if (expandFrom.includes('left')) {
    align = 'start';
  } else if (expandFrom.includes('center')) {
    align = 'center';
  }

  useEffect(() => {
    setTimeout(() => {
      isMounted.current = true;
    }, 0);
  }, []);

  return (
    <Transition in={isOpen} timeout={{ enter: 0, exit: 200 }} mountOnEnter unmountOnExit>
      {(animationState) => (
        <RadixPopover.Content
          className={cn(styles.menuContainer, styles[`from-${expandFrom}`], styles[`side-${side}`])}
          style={menuStyle}
          onKeyDown={handleMenuKeyDown}
          forceMount
          // @ts-ignore
          align={align}
          side={side}
          onInteractOutside={onInteractOutside}
        >
          <div
            className={cn(
              styles.menu,
              styles[`menu-${animationState}`],
              styles[`from-${expandFrom}`],
              styles.popoverMenu,
              menuClassName,
            )}
          >
            {children}
          </div>
        </RadixPopover.Content>
      )}
    </Transition>
  );
}
